import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import { actorSchema } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useMutation, useQueryClient } from "@-/com.npmjs.tanstack__react-query";
import { isNull } from "@-/com.npmjs.drizzle-orm";
import { requestLearnReset } from "@-/dev.improve.haniverse.routes.nav.app.learn";
import { clearPendingCompletedSlideKeys } from "@-/dev.improve.haniverse.lib.slide.read";

export function useResetOnboarding() {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (c) => c.db);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<void> => {
      clearPendingCompletedSlideKeys();
      const nowIso = new Date().toISOString();
      await actorDb(import.meta.location)
        .update(actorSchema.userSlide)
        .set({ deletedAt: nowIso })
        .where(isNull(actorSchema.userSlide.deletedAt));
    },
    mutationKey: ["resetOnboarding"],
    onMutate: async () => {
      clearPendingCompletedSlideKeys();
      await Promise.all([
        queryClient.cancelQueries({ queryKey: ["actorQuery", "userSlide"] }),
        queryClient.cancelQueries({ queryKey: ["completedSlideKeys"] }),
        queryClient.cancelQueries({ queryKey: ["nextLesson"] }),
        queryClient.cancelQueries({ queryKey: ["learnSummaryPage"] }),
      ]);
      const previousCompletedSlideKeysQueries = queryClient.getQueriesData<Set<string>>({
        queryKey: ["completedSlideKeys"],
      });
      queryClient.setQueriesData<Set<string>>(
        { queryKey: ["completedSlideKeys"] },
        () => new Set(),
      );
      // Do not reset the Learn navigator until the soft-delete succeeds. Resetting here made the
      // summary resolve against the still-completed slide rows, so a user with a due card landed on
      // "Welcome Back" and stayed there even after the mutation completed.
      return { previousCompletedSlideKeysQueries };
    },
    // Pop the Learn tab's Stack back to its index root so the just-restored intro slides
    // show immediately, even though `freezeOnBlur` otherwise keeps the tab frozen on whatever
    // lesson route was open. The index re-resolves to the first slide off its live
    // `userSlide`-keyed query. Runs in addition to any call-site `onSuccess` (e.g. the toast).
    onError: (_error, _variables, context) => {
      for (const [queryKey, completedSlideKeys] of context?.previousCompletedSlideKeysQueries ??
        []) {
        queryClient.setQueryData(queryKey, completedSlideKeys);
      }
    },
    onSuccess: async () => {
      clearPendingCompletedSlideKeys();
      // `onMutate` cancels the summary/lesson reads so they cannot win the soft-delete race, but a
      // cancelled query keeps its previous data. Refetch every dependent key before resetting the
      // Learn stack; otherwise the freshly reset navigator can render the cached due-review summary
      // instead of the restored welcome slide until some unrelated invalidation occurs.
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["actorQuery", "userSlide"],
          refetchType: "all",
        }),
        queryClient.invalidateQueries({ queryKey: ["completedSlideKeys"], refetchType: "all" }),
        queryClient.invalidateQueries({ queryKey: ["nextLesson"], refetchType: "all" }),
        queryClient.invalidateQueries({ queryKey: ["learnSummaryPage"], refetchType: "all" }),
      ]);
      requestLearnReset();
    },
  });
}
